import {
  VegaBox,
  VegaButton,
  VegaChip,
  VegaFlex,
  VegaFont,
  VegaPageNotification,
  VegaSectionTitle,
} from '@globalpayments/vega-react';
import { useRef, useState } from 'react';

import statusApiFunctions from '../../api/status';
import useComponentStatusData from '../../hooks/useComponentStatusData';
import { ComponentStatus, ComponentStatusCheck, ComponentStatusCheckResult } from '../../types/componentStatus';

import styles from './index.module.css';
import ComponentStatusDetailsModal from './components/ComponentStatusDetailsModal';
import ComponentStatusGroup from './components/ComponentStatusGroup';
import PageSkeleton from '../pageSkeleton';

const ComponentStatuses = () => {
  const [statusComponents, setStatusComponents] = useState<Array<ComponentStatusCheck>>();
  const [lastRefreshTime, setLastRefreshTime] = useState<string>();
  const [showDescModal, setShowDescModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<ComponentStatusCheck>();
  const refreshId = useRef(0);

  const syncStatusChecks = (data: unknown) => {
    if (Array.isArray(data)) {
      const currentRefreshId = ++refreshId.current;
      const previousResults = new Map(
        statusComponents?.map((component) => [component.componentStatusCheckId, component.statusResult]),
      );
      const pendingComponents = data.map((component: ComponentStatusCheck) => ({
        ...component,
        isLoadingStatus: true,
        statusResult: previousResults.get(component.componentStatusCheckId) ?? component.statusResult,
      }));

      setStatusComponents(pendingComponents);
      pendingComponents.forEach(async (endpoint) => {
        const result = (await statusApiFunctions.checkComponentStatus(
          endpoint.componentStatusCheckId,
        )) as ComponentStatusCheckResult;
        if (refreshId.current !== currentRefreshId) return;

        setStatusComponents((components) =>
          components?.map((component) =>
            component.componentStatusCheckId === endpoint.componentStatusCheckId
              ? { ...component, isLoadingStatus: false, statusResult: result }
              : component,
          ),
        );
      });
      setLastRefreshTime(new Date().toLocaleString());
    }
  };

  const { isFetching, isLoading, isError, refetch } = useComponentStatusData(syncStatusChecks);

  const openDescriptionModal = (component: ComponentStatusCheck) => {
    setSelectedComponent(component);
    setShowDescModal(true);
  };

  const groupedComponents = new Map<string, ComponentStatusCheck[]>();
  statusComponents?.forEach((component) => {
    const group = groupedComponents.get(component.groupName) ?? [];
    group.push(component);
    groupedComponents.set(component.groupName, group);
  });

  const statusCounts = statusComponents?.reduce(
    (counts, component) => {
      if (component.isLoadingStatus) counts.checking += 1;

      if (component.statusResult?.status === ComponentStatus.Healthy) {
        counts.healthy += 1;
      } else if (component.statusResult?.status === ComponentStatus.Unhealthy) {
        counts.unhealthy += 1;
      } else if (component.statusResult?.status === ComponentStatus.Unknown) {
        counts.unknown += 1;
      }
      return counts;
    },
    { checking: 0, healthy: 0, unhealthy: 0, unknown: 0 },
  ) ?? { checking: 0, healthy: 0, unhealthy: 0, unknown: 0 };

  const loadErrorMessage = 'An error has occurred loading the status check endpoint statuses';

  return (
    <VegaBox className={styles.statusCheckDashboardContainer}>
      <VegaFlex direction="col" gap="size-32">
        <VegaFlex alignItems="center" flexWrap="wrap" gap="size-16" justifyContent="space-between">
          <VegaSectionTitle titleText="Component Status Checks" />
          {!isLoading && !isError && statusComponents && (
            <VegaFlex alignItems="center" flexWrap="wrap" gap="size-16">
              <VegaFont as="span" color="text-secondary" variant="font-p2-short">
                Last refreshed: {lastRefreshTime}
              </VegaFont>
              <VegaButton
                aria-label="RefreshButton"
                icon="fa-solid fa-rotate"
                label="Refresh All"
                loading={isFetching}
                onVegaClick={() => refetch()}
                size="small"
                variant="secondary"
              />
            </VegaFlex>
          )}
        </VegaFlex>

        {!isLoading && !isError && statusComponents && (
          <VegaFlex flexWrap="wrap" gap="size-8">
            <VegaChip
              chipType="status"
              size="small"
              statusColor="bg-status-success"
              text={`Healthy ${statusCounts.healthy}`}
            />
            <VegaChip
              chipType="status"
              size="small"
              statusColor="bg-status-error"
              text={`Unhealthy ${statusCounts.unhealthy}`}
            />
            <VegaChip
              chipType="status"
              size="small"
              statusColor="bg-status-warning"
              text={`Unknown ${statusCounts.unknown}`}
            />
            <VegaChip
              chipType="status"
              size="small"
              statusColor="bg-status-info"
              text={`Checking ${statusCounts.checking}`}
            />
          </VegaFlex>
        )}

        {isLoading || (!statusComponents && !isError) ? (
          <PageSkeleton label="Loading component statuses" variant="table" />
        ) : isError ? (
          <VegaPageNotification
            duration={0}
            message={loadErrorMessage}
            notificationTitle="Component status checks unavailable"
            type="error"
          />
        ) : statusComponents && groupedComponents.size > 0 ? (
          Array.from(groupedComponents, ([groupName, components]) => (
            <ComponentStatusGroup
              components={components}
              groupName={groupName}
              key={groupName}
              onOpenDetails={openDescriptionModal}
            />
          ))
        ) : (
          <VegaPageNotification
            duration={0}
            message="No component status checks are configured for this environment."
            notificationTitle="No component checks"
            type="info"
          />
        )}
      </VegaFlex>

      <ComponentStatusDetailsModal
        component={selectedComponent}
        onClose={() => setShowDescModal(false)}
        open={showDescModal}
      />
    </VegaBox>
  );
};

export default ComponentStatuses;
